
import { Options } from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import FormMixin from "../../helpers/mixins/form";
import { Employee, SystemApi, EmployeeApi, ContractTypeEnum, EnrollmentEnum, Department } from "../../autogen";
import { Field, Form, FormActions } from "vee-validate";
import FieldLabel from "../atoms/FieldLabel.vue";
import * as yup from "yup";
import FieldError from "../atoms/FieldError.vue";
import HelpIcon from "../atoms/HelpIcon.vue";
import FormError from "../atoms/FormError.vue";
import ActionButton from "@/components/atoms/ActionButton.vue";
import Avatar from "../atoms/Avatar.vue";
import Multiselect from "@vueform/multiselect";
import moment from "moment-timezone";
import Datepicker from "vue3-datepicker";
import { ja } from "date-fns/locale";

type FormValues = { [P in keyof Employee]: Employee[P] };
@Options({
  components: {
    Field,
    Form,
    FieldLabel,
    FieldError,
    ActionButton,
    Avatar,
    FormError,
    Multiselect,
    Datepicker,
    HelpIcon
  }
})
export default class EmployeeFormFields extends FormMixin {
  @Prop({ type: Object }) employee?: Employee;
  @Prop({ type: String, required: true }) title!: string;
  @Prop({ type: String, required: true }) formName = "";
  @Prop({ type: Function, required: true }) api!: Function;
  formValues: Partial<FormValues> = {};
  acceptImageType = ["image/png", "image/jpeg"];
  validationSchema!: yup.AnyObjectSchema;
  // DyanmoDB maximum item size is 400KB and base64 format is bigger than original
  isEditable = false;
  enrollments = Object.entries(EnrollmentEnum).map(([id, name]) => ({ id, name }));
  enrollmentRetirement = "";
  retirementReasonOptions = {
    ...this.multiSelectBaseOptions,
    valueProp: "code",
    max: 3,
    options: async () => (await new SystemApi().getRetirementReasons({})).data
  };
  retirementDate: Date | null = null;
  contractOptions = Object.entries(ContractTypeEnum).map(([code, name]) => ({ code, name }));
  contractEmployee = "";
  addAdditionalDepartments = false;
  addAnotherDepartments = false;
  locale = ja;
  departments: Department[] = this.storeInstance.getDepartmentHierarchy({ visibilityMyself: true, withoutAll: true })[1];
  departmentOptions = {
    ...this.multiSelectBaseOptions,
    max: 3
  };
  isSending = false;
  sendComplete = false;
  sendMassage = "送信しました";
  noSendInviteMailFlg = false;

  async beforeCreate() {
    this.validationSchema = yup.object({
      username: yup
        .string()
        .trim()
        .required(this.$t("input_required", { field: "ユーザーID" })),
      firstName: yup
        .string()
        .trim()
        .required(this.$t("input_required", { field: "名" })),
      lastName: yup
        .string()
        .trim()
        .required(this.$t("input_required", { field: "姓" })),
      email: yup
        .string()
        .required(this.$t("input_required", { field: "メールアドレス" }))
        .email(this.$t("input_email")),
      contractType: yup.string().required(this.$t("input_required", { field: "雇用形態" })),
      enrollment: yup.string().required(this.$t("input_required", { field: "在籍状況" })),
      retirementDate: yup.string().when(["enrollment"], {
        is: (enrollment: string) => ["retiring", "retired"].includes(enrollment),
        then: yup
          .string()
          .nullable(true)
          .required(this.$t("input_required", { field: "退職日" }))
      }),
      retirementReasons: yup.array().when(["enrollment"], {
        is: (enrollment: string) => ["retiring", "retired"].includes(enrollment),
        then: yup
          .array()
          .nullable(true)
          .min(1, this.$t("input_required", { field: "退職理由" }))
      }),
      departmentId: yup.string().required(this.$t("input_required", { field: "部署" })),
      occupationId: yup.string().required(this.$t("input_required", { field: "職種" })),
      role: yup.string().required(this.$t("input_required", { field: "権限" }))
    });
  }
  async beforeMount() {
    await this.setRoles();
    this.checkEditableField();
    if (this.employee) this.updatedEmployeeField();
    this.stateDepartmentsChange();
  }
  @Watch("employee")
  updatedEmployeeField() {
    this.clearForm();
    this.formValues = this.employee
      ? Object.assign(this.employee, {
          occupationId: this.employee.occupationId,
          positionId: this.employee.positionId,
          retirementDate: new Date(String(this.employee.retirementDate)),
          email: this.employee.email ? this.employee.email : ""
        })
      : {
          username: "",
          lastName: "",
          firstName: "",
          email: "",
          career: "new",
          departmentId: "",
          occupationId: "",
          positionId: "",
          role: "4",
          contractType: "employee",
          enrollment: "enrolled",
          retirementReasons: [],
          retirementDate: ""
        };
    this.checkEditableField();
    this.contractEmployee = (this.formValues?.contractType as string) || "";
    this.enrollmentRetirement = this.employee?.enrollment || "";
    this.addAdditionalDepartments = Boolean(this.formValues.aditionalDepartmentIds);
    this.addAnotherDepartments = Boolean(this.formValues.anotherDepartmentIds);
    this.stateDepartmentsChange();
  }
  clearForm() {
    if (!this.$refs.form) return;
    const formValidation = this.$refs.form as FormActions<yup.AnyObjectSchema>;
    formValidation.resetForm();
    this.globalErrors = [];
  }
  async onBeforeSubmit(formValues: FormValues) {
    await this.onSubmit(async () => {
      const values = Object.assign({}, formValues, {
        career: ["employee"].includes(formValues.contractType as string) ? formValues.career : "",
        retirementDate: ["retiring", "retired"].includes(this.enrollmentRetirement)
          ? moment(this.formValues.retirementDate).format("YYYY-MM-DD")
          : "",
        retirementReasons: ["retiring", "retired"].includes(this.enrollmentRetirement) ? this.formValues.retirementReasons : []
      });
      // when new employee create, choose send invite mail.
      if (!this.employee?.id) Object.assign(values, { noSendInviteMailFlg: this.noSendInviteMailFlg });
      await this.api(values);
      this.$emit("submitted");
    });
  }
  async onDeleteButtonClick() {
    this.onDelete(async () => {
      await new EmployeeApi().deleteEmployee({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        departmentId: this.employee!.departmentId,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        employeeId: this.employee!.id
      });
    }, "従業員を削除します。よろしいでしょうか？\n※削除した場合、従業員は完全に削除されます。\n※適性検査のデータも全て表示されなくなります。");
  }
  checkEditableField() {
    this.isEditable = this.$role.isEditable("employee");
  }
  async onResendMailButtonClick(event: Event) {
    if (this.isSending) return;
    this.isSending = true;
    try {
      await new SystemApi().resendInviteMail({ employeeId: this.employee!.id });
    } catch (e) {
      if (e.response.status === 412) {
        this.sendMassage = `時間をおいて再度お試しください`;
      } else {
        this.sendMassage = `不明なエラーが発生しました`;
      }
    } finally {
      this.isSending = false;
    }
    this.sendComplete = true;
    setTimeout(() => {
      this.sendComplete = false;
    }, 2000);
  }
  @Watch("storeInstance.state.departments")
  stateDepartmentsChange() {
    this.departments = this.storeInstance.getDepartmentHierarchy({ visibilityMyself: true, withoutAll: true })[1];
  }
}
